import { supabase } from "@/integrations/supabase/client";

export interface HourTotals {
  approved: number;
  open: number;
  total: number;
}

export const emptyTotals = (): HourTotals => ({ approved: 0, open: 0, total: 0 });

export const fmtHours = (h: number) =>
  new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(h);

export const fmtEur = (v: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(v);

/**
 * Aggregates hours per order. Approved hours come from the order's stored
 * counter (orders.approved_hours), which a database trigger updates on every
 * approval. Open hours (draft/sent) are aggregated live, rejected are ignored.
 */
export const loadOrderHourTotals = async (): Promise<Record<string, HourTotals>> => {
  const [{ data: orderRows, error: ordersError }, { data, error }] = await Promise.all([
    supabase.from("orders").select("id, approved_hours"),
    supabase.from("timesheets").select("order_id, status, timesheet_items(hours)"),
  ]);
  if (ordersError || error || !data) return {};

  const map: Record<string, HourTotals> = {};
  for (const o of orderRows ?? []) {
    map[o.id] = { approved: Number(o.approved_hours ?? 0), open: 0, total: 0 };
  }
  for (const row of data) {
    const orderId = row.order_id;
    if (!orderId) continue;
    const hours = (row.timesheet_items ?? []).reduce((acc, i) => acc + Number(i.hours), 0);
    const totals = (map[orderId] ??= emptyTotals());
    if (row.status !== "approved" && row.status !== "rejected") totals.open += hours;
    totals.total = totals.approved + totals.open;
  }
  return map;
};

export const sumTotals = (list: HourTotals[]): HourTotals =>
  list.reduce(
    (acc, t) => ({ approved: acc.approved + t.approved, open: acc.open + t.open, total: acc.total + t.total }),
    emptyTotals(),
  );
